import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// Use service layer instead of direct Firestore access
import { getAllFeatures, createFeature } from '../../services/featureService';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';

const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
}));

const FeatureList = () => {
  const navigate = useNavigate();
  const [features, setFeatures] = useState([]);
  const [open, setOpen] = useState(false);
  const [newFeature, setNewFeature] = useState({
    name: '',
    description: '',
    key: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeatures();
  }, []);

  const fetchFeatures = async () => {
    try {
      // Use the service layer instead of direct Firestore access
      const response = await getAllFeatures();
      
      // The API returns { features: [...] }, so extract the features array
      if (response && response.features && Array.isArray(response.features)) {
        setFeatures(response.features);
      } else {
        console.warn('Unexpected features data format:', response);
        setFeatures([]);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching features:', error);
      setFeatures([]);
      setLoading(false);
    }
  };

  const handleAddFeature = async () => {
    try {
      // Use the service layer instead of direct Firestore access
      await createFeature({
        ...newFeature,
        createdAt: new Date(),
        enabled: true
      });
      
      setOpen(false);
      setNewFeature({ name: '', description: '', key: '' });
      fetchFeatures();
    } catch (error) {
      console.error('Error adding feature:', error);
    }
  };

  const handleConfigure = (featureId) => {
    navigate(`/admin/features/${featureId}/configure`);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="container">
      <HeaderCard>
        <CardContent sx={{ py: 1.5, px: 3, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Feature Flags
          </Typography>
        </CardContent>
      </HeaderCard>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h1>Feature Configuration</h1>
        <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
          Add New Feature
        </Button>
      </div>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Key</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(features) && features.map((feature) => (
              <TableRow key={feature.id}>
                <TableCell>{feature.name}</TableCell>
                <TableCell>{feature.key}</TableCell>
                <TableCell>{feature.description}</TableCell>
                <TableCell>{feature.enabled ? 'Enabled' : 'Disabled'}</TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => handleConfigure(feature.id)}
                  >
                    Configure
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Add New Feature</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Feature Name"
            fullWidth
            value={newFeature.name}
            onChange={(e) => setNewFeature({ ...newFeature, name: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Feature Key"
            fullWidth
            value={newFeature.key}
            onChange={(e) => setNewFeature({ ...newFeature, key: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
            helperText="This will be used in code to check if feature is enabled"
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={4}
            value={newFeature.description}
            onChange={(e) => setNewFeature({ ...newFeature, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleAddFeature} color="primary">
            Add Feature
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FeatureList; 